import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GoPlusCircle } from "react-icons/go";
import { FiMinusCircle } from "react-icons/fi";
import {
  calculateNights,
  calculateRowGst,
  numberToWords,
} from "./invoiceUtils";
import { API_URL } from "../../Config";

const emptyRow = () => ({
  particulars: "",
  checkin: "",
  checkout: "",
  nights: "",
  rate: "",
  cgst: "",
  sgst: "",
});

// Check-in pachi ni next date get krva mate helper function
const getNextDate = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return "";
  const d = new Date(
    parseInt(parts[0]),
    parseInt(parts[1]) - 1,
    parseInt(parts[2]),
  );
  d.setDate(d.getDate() + 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const ProformaEdit = ({ proformaid: proformaidProp, onUpdate }) => {
  const navigate = useNavigate();
  const { proformaid: proformaidParam } = useParams();
  const proformaid = proformaidProp || proformaidParam;

  const [rows, setRows] = useState([emptyRow()]);
  const [invoiceno, setinvoiceno] = useState("");
  const [invoicedate, setinvoicedate] = useState("");
  const [guestname, setguestname] = useState("");
  const [guestaddress, setguestaddress] = useState("");
  const [guestgstin, setguestgstin] = useState("");
  const [amountinwords, setamountinwords] = useState("");
  const [subtotal, setSubtotal] = useState("");
  const [gst, setGst] = useState("");
  const [total, setTotal] = useState("");
  const [advanceReceived, setAdvanceReceived] = useState("");
  const [balanceAmount, setBalanceAmount] = useState("");
  const [securityDeposit, setSecurityDeposit] = useState("");
  const [excludeGst, setExcludeGst] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!proformaid) {
      setIsLoading(false);
      return;
    }

    const fetchProforma = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const res = await fetch(`${API_URL}/billing/proforma/${proformaid}`);
        const data = await res.json();

        if (!res.ok || !data.success) {
          setLoadError(data.message || "Failed to load proforma invoice.");
          return;
        }

        const inv = data.invoice || data.proforma || data.data;
        if (!inv) {
          setLoadError("Proforma invoice data not found.");
          return;
        }

        setinvoiceno(inv.invoiceno || "");
        setinvoicedate(
          inv.invoicedate ? String(inv.invoicedate).split("T")[0] : "",
        );
        setguestname(inv.guestname || "");
        setguestaddress(inv.guestaddress || inv.address || "");
        setguestgstin(inv.guestgstin || inv.gstin || "");
        setamountinwords(inv.amountinwords || "");
        setSubtotal(inv.subtotal ?? "");
        setGst(inv.gst ?? "");
        setTotal(inv.total ?? "");
        setAdvanceReceived(inv.advanceReceived ?? "");
        setBalanceAmount(inv.balanceAmount ?? "");
        setSecurityDeposit(inv.securityDeposit ?? "");

        const savedExcludeGst =
          inv.excludeGst ?? inv.excludeGST ?? inv.exclude_gst;

        const hasSavedExcludeGst =
          savedExcludeGst !== undefined && savedExcludeGst !== null;

        const isExcludeGst = hasSavedExcludeGst
          ? savedExcludeGst === true ||
            savedExcludeGst === 1 ||
            savedExcludeGst === "1" ||
            String(savedExcludeGst).toLowerCase() === "true"
          : Number(inv.gst || 0) === 0 &&
            (inv.items || []).every(
              (item) =>
                Number(item.cgst || 0) === 0 && Number(item.sgst || 0) === 0,
            );

        setExcludeGst(isExcludeGst);

        setRows(
          inv.items && inv.items.length > 0
            ? inv.items.map((item) => ({
                particulars: item.particulars || "",
                checkin: item.checkin ? String(item.checkin).split("T")[0] : "",
                checkout: item.checkout
                  ? String(item.checkout).split("T")[0]
                  : "",
                nights: item.nights ?? "",
                rate: item.rate ?? "",
                cgst: item.cgst ?? "",
                sgst: item.sgst ?? "",
              }))
            : [emptyRow()],
        );
      } catch (err) {
        console.error("Error loading proforma invoice:", err);
        setLoadError(
          "Something went wrong while loading the proforma invoice.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchProforma();
  }, [proformaid]);

  const addRow = () => {
    if (rows.length < 6) {
      setRows([...rows, emptyRow()]);
    }
  };

  const removeRow = (indexToRemove) => {
    setRows(rows.filter((_, index) => index !== indexToRemove));
  };

  const updateRow = (index, field, value) => {
    setRows((prevRows) =>
      prevRows.map((row, i) => {
        if (i !== index) return row;

        let updated = { ...row, [field]: value };

        // Jo checkin change thay, ane checkout jo checkin ke te karata pela ni date hoy to checkout reset kri devu
        if (field === "checkin") {
          if (updated.checkout && updated.checkout <= value) {
            updated.checkout = "";
            updated.nights = "";
          }
        }

        if (field === "checkin" || field === "checkout") {
          const nights = calculateNights(updated.checkin, updated.checkout);
          if (nights !== null) {
            updated.nights = nights;
          }
        }

        if (
          field === "rate" ||
          field === "nights" ||
          field === "checkin" ||
          field === "checkout"
        ) {
          const calculatedGst = calculateRowGst(updated.rate, updated.nights);
          if (calculatedGst) {
            updated.cgst = calculatedGst.cgst;
            updated.sgst = calculatedGst.sgst;
          }
        }

        return updated;
      }),
    );
  };

  useEffect(() => {
    const newSubtotal = rows.reduce(
      (sum, r) => sum + (parseFloat(r.rate) || 0) * (parseFloat(r.nights) || 0),
      0,
    );

    if (excludeGst) {
      setSubtotal(newSubtotal ? newSubtotal.toFixed(2) : "");
      setGst("0.00");
      setTotal(newSubtotal ? newSubtotal.toFixed(2) : "");
    } else {
      const newGst = rows.reduce(
        (sum, r) => sum + (parseFloat(r.cgst) || 0) + (parseFloat(r.sgst) || 0),
        0,
      );
      const newTotal = newSubtotal + newGst;

      setSubtotal(newSubtotal ? newSubtotal.toFixed(2) : "");
      setGst(newGst ? newGst.toFixed(2) : "");
      setTotal(newTotal ? newTotal.toFixed(2) : "");
    }
  }, [rows, excludeGst]);

  useEffect(() => {
    const numericTotal = parseFloat(total) || 0;
    setamountinwords(numericTotal ? numberToWords(numericTotal) : "");
  }, [total]);

  const handleCancel = () => {
    const confirmed = window.confirm("Sure want to discard changes?");
    if (confirmed) {
      navigate("/bills");
    }
  };

  const handleUpdate = async () => {
    if (!proformaid) {
      alert("No proforma invoice selected to update.");
      return;
    }
    if (!invoiceno || !invoicedate || !guestname) {
      alert("Invoice No, Invoice Date and Guest Name are required.");
      return;
    }
    if (rows.length === 0) {
      alert("Invoice must contain at least one item.");
      return;
    }

    const payload = {
      invoiceno,
      invoicedate,
      guestname,
      guestaddress,
      guestgstin,
      amountinwords,
      subtotal: subtotal || 0,
      gst: excludeGst ? 0 : gst || 0,
      total: total || 0,
      excludeGst,
      advanceReceived: advanceReceived || 0,
      balanceAmount: balanceAmount || 0,
      securityDeposit: securityDeposit || 0,
      items: rows.map((row) => ({
        particulars: row.particulars,
        checkin: row.checkin || null,
        checkout: row.checkout || null,
        nights: row.nights || 0,
        rate: row.rate || 0,
        cgst: excludeGst ? 0 : row.cgst || 0,
        sgst: excludeGst ? 0 : row.sgst || 0,
      })),
    };

    try {
      setIsSubmitting(true);
      const response = await fetch(
        `${API_URL}/billing/proforma/${proformaid}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        alert(data.message || "Failed to update proforma invoice.");
        return;
      }

      alert("Proforma Invoice updated successfully!");
      if (onUpdate) onUpdate(data);
      navigate("/bills");
    } catch (err) {
      console.error("Error updating proforma invoice:", err);
      alert("Something went wrong while updating the proforma invoice.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
        Loading proforma invoice...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-5xl mx-auto p-10 text-center text-red-600">
        {loadError}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-10 bg-white text-gray-800 font-sans print:p-0">
      <style>{`
        @media print {
          @page { margin: 0; }
          body { margin: 1.6cm; }
          .no-print { display: none; }
        }
      `}</style>

      {/* With or without gst */}
      <div
        onClick={() => setExcludeGst(!excludeGst)}
        className="no-print bg-gray-50 p-2 mb-10 border border-gray-200 flex justify-between items-center rounded cursor-pointer"
      >
        <div
          className="flex items-center gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            id="topExcludeGst"
            checked={excludeGst}
            onChange={(e) => setExcludeGst(e.target.checked)}
            className="w-5 h-5 cursor-pointer"
          />
          <label
            htmlFor="topExcludeGst"
            className="montserrat text-sm font-bold cursor-pointer text-[#333]"
          >
            Does This Include GST ?
          </label>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExcludeGst(!excludeGst);
          }}
          className="bg-gray-200 text-black px-2 py-1 rounded-full text-xs font-bold tracking-wide transition-all"
        >
          {excludeGst ? "Without GST" : "With GST"}
        </button>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="montserrat text-5xl font-extrabold tracking-tight leading-none mb-1 text-[#111]">
            PROFORMA
          </h1>
          <h1 className="montserrat text-5xl font-extrabold tracking-tight mb-2 text-[#111]">
            INVOICE
          </h1>
        </div>
        <div className="w-20 h-20 rounded-full border border-[#111] flex items-center justify-center p-1 bg-[#111]">
          <img
            src="https://zorbastays.com/android-chrome-512x512.png"
            alt="Zorba Stays Logo"
            className="w-full h-full object-contain rounded-full"
          />
        </div>
      </div>

      {/* Invoice Meta */}
      <div className="flex gap-16 mb-6">
        <div className="flex flex-col gap-1">
          <p className="montserrat text-[10px] font-bold uppercase tracking-wider text-[#555] m-0">
            INVOICE NO.
          </p>
          <input
            type="text"
            className="w-24 p-0 border-0 bg-transparent outline-none font-semibold text-sm focus:ring-0 text-[#111]"
            placeholder="00"
            value={invoiceno}
            onChange={(e) => setinvoiceno(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <p className="montserrat text-[10px] font-bold uppercase tracking-wider text-[#555] m-0">
            DATE
          </p>
          <input
            type="date"
            className="p-0 border-0 bg-transparent outline-none font-semibold text-sm focus:ring-0 text-[#111]"
            value={invoicedate}
            onChange={(e) => setinvoicedate(e.target.value)}
          />
        </div>
      </div>

      <div className="border-t border-[#111] mb-6"></div>

      {/* Bill To & From Section */}
      <div className="flex justify-between items-start mb-8 text-sm">
        <div className="w-1/2 flex flex-col gap-1">
          <p className="montserrat text-[10px] font-bold uppercase tracking-wider text-[#555]">
            BILL TO
          </p>
          <input
            type="text"
            className="w-full font-bold text-base p-0 border-0 bg-transparent outline-none focus:ring-0 text-[#111]"
            placeholder="Guest Name"
            value={guestname}
            onChange={(e) => setguestname(e.target.value)}
          />

          <div className="flex items-center w-full text-xs text-[#444]">
            <label className="montserrat whitespace-nowrap font-medium text-[#555]">
              GSTIN NO. :
            </label>

            <input
              type="text"
              className="montserrat flex-1 min-w-0 text-xs text-[#444] p-0 ml-1 border-0 bg-transparent outline-none focus:ring-0"
              placeholder=""
              value={guestgstin}
              maxLength={15}
              onChange={(e) => setguestgstin(e.target.value.toUpperCase())}
            />
          </div>
          <input
            type="text"
            className="w-full text-xs text-[#444] p-0 border-0 bg-transparent outline-none focus:ring-0"
            placeholder="Address"
            value={guestaddress}
            onChange={(e) => setguestaddress(e.target.value)}
          />
        </div>

        <div className="w-1/2 text-right flex flex-col gap-1">
          <p className="montserrat text-[10px] font-bold uppercase tracking-wider text-[#555]">
            FROM
          </p>
          <p className="montserrat font-extrabold text-base text-[#111]">
            Zorba Stays
          </p>
          <p className="montserrat text-xs text-[#444]">
            GSTIN NO.: 24AADFZ2976K1ZO
          </p>
          <p className="montserrat text-xs text-[#444] leading-relaxed">
            306-7 - Business Centrum,
            <br />
            Amin Marg,
            <br />
            Rajkot, 360001
            <br />
            India
          </p>
        </div>
      </div>

      {/* Table Design */}
      <table className="w-full border-collapse border-t border-b border-[#111] text-center text-xs">
        <thead>
          <tr className="montserrat uppercase font-bold border-b border-[#111] text-[11px] text-[#333]">
            <th className="py-3 w-[50px] text-left pl-2">SR. NO.</th>
            <th className="py-3 text-left">PARTICULARS</th>
            <th className="py-3">PERIOD OF STAY</th>
            <th className="py-3">NO. OF NIGHTS</th>
            <th className="py-3">PER NIGHT RATE</th>
            {!excludeGst && <th className="py-3">CGST</th>}
            {!excludeGst && <th className="py-3">SGST</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={index}
              className="montserrat border-b border-[#111] text-xs"
            >
              <td className="py-3 text-left pl-2 font-medium">{index + 1}</td>
              <td className="py-3 px-2 text-left">
                <input
                  className="w-full text-left outline-none bg-transparent text-[#111]"
                  placeholder="Villa Name"
                  value={row.particulars}
                  onChange={(e) =>
                    updateRow(index, "particulars", e.target.value)
                  }
                />
              </td>
              <td className="py-3 px-2">
                <div className="flex justify-center items-center gap-1">
                  <input
                    type="date"
                    className="w-full text-center outline-none border-none bg-transparent focus:ring-0 text-xs text-[#111]"
                    value={row.checkin}
                    onChange={(e) =>
                      updateRow(index, "checkin", e.target.value)
                    }
                  />
                  <span>to</span>
                  <input
                    type="date"
                    className="w-full text-center outline-none border-none bg-transparent focus:ring-0 text-xs text-[#111]"
                    value={row.checkout}
                    min={getNextDate(row.checkin)} // Check-in date ni pachi ni date j (e.g. 5 hoy to min 6) select thase
                    onChange={(e) =>
                      updateRow(index, "checkout", e.target.value)
                    }
                  />
                </div>
              </td>
              <td className="py-3">
                <input
                  className="w-full text-center outline-none bg-transparent text-[#111]"
                  placeholder="0"
                  value={row.nights}
                  onChange={(e) => updateRow(index, "nights", e.target.value)}
                />
              </td>
              <td className="py-3">
                <input
                  className="w-full text-center outline-none bg-transparent text-[#111]"
                  placeholder="0.00"
                  value={row.rate}
                  onChange={(e) => updateRow(index, "rate", e.target.value)}
                />
              </td>
              {!excludeGst && (
                <td className="py-3">
                  <input
                    className="w-full text-center outline-none bg-transparent text-[#111]"
                    placeholder="0.00"
                    value={row.cgst}
                    onChange={(e) => updateRow(index, "cgst", e.target.value)}
                  />
                </td>
              )}
              {!excludeGst && (
                <td className="py-3">
                  <input
                    className="w-full text-center outline-none bg-transparent text-[#111]"
                    placeholder="0.00"
                    value={row.sgst}
                    onChange={(e) => updateRow(index, "sgst", e.target.value)}
                  />
                </td>
              )}
              <td className="py-3 no-print">
                {index >= 1 && (
                  <button
                    onClick={() => removeRow(index)}
                    className="px-2 py-1 hover:scale-110 text-red-600"
                  >
                    <FiMinusCircle size={16} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add Button */}
      <div className="my-2 no-print">
        <button
          onClick={addRow}
          disabled={rows.length >= 6}
          className={`flex items-center gap-1 text-xs font-semibold ${rows.length >= 6 ? "opacity-50 cursor-not-allowed" : "hover:scale-105"}`}
        >
          <GoPlusCircle size={18} /> Add Item
        </button>
      </div>

      {/* Totals Section */}
      <div className="w-full mt-6 px-2 text-sm text-[#111]">
        {/* Sub Total */}
        {!excludeGst && (
          <div className="montserrat flex justify-end items-center py-1.5 font-medium">
            <span className="w-40 text-left text-[#555]">Amount Due</span>
            <input
              type="text"
              className="w-32 text-right outline-none border-none bg-transparent focus:ring-0 text-[#111] p-0"
              placeholder="0.00"
              value={subtotal}
              onChange={(e) => setSubtotal(e.target.value)}
            />
          </div>
        )}

        {/* GST */}
        {!excludeGst && (
          <div className="montserrat flex justify-end items-center py-1.5 font-medium">
            <span className="w-40 text-left text-[#555]">GST(18%)</span>
            <input
              type="text"
              className="w-32 text-right outline-none border-none bg-transparent focus:ring-0 text-[#111] p-0"
              placeholder="0.00"
              value={gst}
              onChange={(e) => setGst(e.target.value)}
            />
          </div>
        )}

        {/* Total */}
        <div className="montserrat flex justify-end items-center py-3 border-t border-b border-[#263238] font-bold">
          <span className="w-40 text-left">Total</span>
          <input
            type="text"
            className="w-32 text-right outline-none border-none bg-transparent focus:ring-0 font-bold text-[#111] p-0"
            placeholder="0.00"
            value={total}
            onChange={(e) => setTotal(e.target.value)}
          />
        </div>

        {/* (Advance, Balance, Security Deposit) */}
        <div className="mt-4 flex flex-col gap-1.5">
          <div className="montserrat flex justify-between items-center py-1 font-medium">
            <span className="text-left text-[#555]">
              Advance to be Received
            </span>
            <input
              type="text"
              className="w-32 text-right outline-none border-none bg-transparent focus:ring-0 text-[#111] p-0"
              placeholder="0.00"
              value={advanceReceived}
              onChange={(e) => setAdvanceReceived(e.target.value)}
            />
          </div>

          <div className="montserrat flex justify-between items-center py-1 font-medium">
            <span className="text-left text-[#555]">Balance amount</span>
            <input
              type="text"
              className="w-32 text-right outline-none border-none bg-transparent focus:ring-0 text-[#111] p-0"
              placeholder="0.00"
              value={balanceAmount}
              onChange={(e) => setBalanceAmount(e.target.value)}
            />
          </div>

          <div className="montserrat flex justify-between items-center py-1 font-medium border-b border-[#263238] pb-3">
            <span className="text-left text-[#555]">Security Deposit</span>
            <input
              type="text"
              className="w-32 text-right outline-none border-none bg-transparent focus:ring-0 text-[#111] p-0"
              placeholder="0.00"
              value={securityDeposit}
              onChange={(e) => setSecurityDeposit(e.target.value)}
            />
          </div>
        </div>

        {/* Amount In Words */}
        <div className="mt-6">
          <p className="montserrat text-[10px] font-medium uppercase tracking-wide text-[#555] mb-1">
            IN WORDS
          </p>

          <input
            type="text"
            className="montserrat w-full p-0 m-0 border-0 outline-none bg-transparent focus:ring-0 text-sm font-extrabold"
            placeholder="Amount in words"
            value={amountinwords}
            onChange={(e) => setamountinwords(e.target.value)}
          />
        </div>
      </div>

      {/* Thank you & Policies / Payment Info */}
      <div className="montserrat mt-10 text-sm font-extrabold">
        <p>Thank you!</p>
        <p>It's been a pleasure hosting you!</p>
      </div>

      <div className="flex justify-between items-start mt-8 text-xs text-[#333] gap-8">
        <div className="w-1/2 flex flex-col gap-2">
          <p className="montserrat font-bold text-[10px] uppercase text-[#555]">
            CANCELLATION POLICY
          </p>
          <ul className="list-disc pl-4 space-y-1">
            <li>
              30 days prior to the check-in date, 100% payment refundable.
            </li>
            <li>
              If cancellation is not made before the 30 days then advance amount
              will be forfeited.
            </li>
          </ul>
        </div>

        <div className="w-1/2 flex flex-col gap-2">
          <p className="montserrat font-bold text-[10px] uppercase text-[#555]">
            PAYMENT INFO
          </p>
          <p className="leading-relaxed text-[#444]">
            HDFC Bank
            <br />
            Ac NO.: 50200077506738
            <br />
            Ac Name: ZORB A STAYS
            <br />
            IFSC: HDFC0000101
            <br />
            Branch: RAJKOT - YAGNIK ROAD - GUJARAT
            <br />
            Account Type: CURRENT
          </p>
          <div className="mt-2 flex items-center gap-4">
            <div className="w-20 h-20 border border-gray-300 p-1 bg-white">
              <img
                src="/"
                alt="QR Code"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
            <div className="text-[11px] mt-12">
              <p className="font-semibold text-[#111]">Name: ZORBA STAYS</p>
              <p className="text-[#555]">UPI ID: 8866262368@hdfcbank</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Branding */}
      <div className="mt-12 flex flex-col gap-2">
        <div className="flex items-center px-0">
          <div className="flex-1 border-t border-[#111]"></div>
          <div className="px-3 flex items-center justify-center">
            <img
              src="../assets/zorba_logo.webp"
              alt="Zorba Logo"
              className="w-7 h-7 object-contain"
            />
          </div>
          <div className="flex-1 border-t border-[#111]"></div>
        </div>

        <div className="montserrat flex justify-between items-center text-xs text-[#555] px-1">
          <span>@zorba_stays</span>
          <span>www.zorbastays.com</span>
        </div>
      </div>

      {/* Update/Cancel Buttons */}
      <div className="flex justify-center gap-4 mt-8 mb-10 no-print">
        <button
          onClick={handleUpdate}
          disabled={isSubmitting}
          className={`px-6 py-2 bg-green-900 rounded-lg text-center text-white text-sm font-semibold hover:bg-green-800 transition-all ${
            isSubmitting ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          {isSubmitting ? "Updating..." : "Update"}
        </button>
        <button
          onClick={handleCancel}
          className="px-6 py-2 bg-gray-800 rounded-lg text-center text-white text-sm font-semibold hover:bg-gray-700 transition-all"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ProformaEdit;
